import { useRouter } from "next/router";
import PropTypes from "prop-types";

import AddTextSetIcon from "@/icons/AddTextSetIcon";
import formatDate from "@/utils/formatDate";
import ActionPopOutOption from "@/view/ActionPopOutOption";
import contentTypes from "@/view/constants/contentTypes";
import getEventPropertiesContentType from "@/view/utils/getEventPropertiesContentType";
import truncateProperty from "@/view/utils/truncateProperty";
import useCreateTextSet from "@/view/utils/useCreateTextSet";
import useCreateTextSetContent from "@/view/utils/useCreateTextSetContent";
import { addActionIconCss } from "../../AddAction.style";

const propTypes = {
  contentId: PropTypes.string,
  contentType: PropTypes.string,
  currentLevel: PropTypes.shape({
    header_id: PropTypes.string,
    id: PropTypes.string,
    language: PropTypes.string,
  }),
  user: PropTypes.object,
};

const CreateNewTextSet = ({ contentId, contentType, currentLevel, user }) => {
  const router = useRouter();
  const [createNewTextSet] = useCreateTextSet();
  const [createTextSetContent] = useCreateTextSetContent();

  const { first_name: userFirstName, last_name: userLastName } = user;

  const contentIsVideo = contentType === contentTypes.INTERACTIVE_VIDEO;

  const sanitizeCreateNewTextSetPayload = () => {
    const dateNow = new Date();
    const dateNowAsISOString = dateNow.toISOString();

    return {
      date_shared: dateNowAsISOString,
      description: `A text set created by ${userFirstName} ${userLastName}`,
      title: `${truncateProperty({ property: userFirstName, maxLength: 60 })}'s Text Set - ${formatDate(dateNow)}`,
    };
  };

  const sanitizeCreateTextSetContentPayload = ({ textSet }) => ({
    container_id: textSet.content_id,
    member_id: contentIsVideo ? contentId : currentLevel?.header_id,
  });

  const navigateToTextSet = ({ textSet }) => {
    const href = {
      pathname: "/subject/other/[textSetId]",
      query: {
        textSetId: textSet.id,
      },
    };
    router.replace(href, null, { shallow: false });
  };

  const handleNewTextSetCreation = async (e) => {
    e.preventDefault();
    try {
      const createNewTextSetPayload = sanitizeCreateNewTextSetPayload();
      const { data } = await createNewTextSet(createNewTextSetPayload);
      const createTextSetContentPayload = sanitizeCreateTextSetContentPayload({
        textSet: data,
      });
      await createTextSetContent(createTextSetContentPayload);

      navigateToTextSet({ textSet: data });
    } catch (_e) {
      // TODO: add error handling to create new text set requests. Will be addressed in a separate ticket
    }
  };

  const eventProperties = {
    objectType: "Content",
    actionPrefix: "Initiate",
    actionName: "Create",
    streamProperties: {
      content_type: getEventPropertiesContentType(contentType),
      description: "Create text set",
      content_id: contentIsVideo ? contentId : currentLevel?.id,
    },
  };

  return (
    <ActionPopOutOption
      title="Create a new text set"
      icon={<AddTextSetIcon css={addActionIconCss} />}
      onClick={handleNewTextSetCreation}
      eventProperties={eventProperties}
      dataProps={{ "data-qa-selector": "create-text-set" }}
    />
  );
};

CreateNewTextSet.propTypes = propTypes;

export default CreateNewTextSet;
